import React, { useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import styled from "styled-components";
import { Close } from "@mui/icons-material";
import debounce from "lodash/debounce";
import DeleteIcon from "@mui/icons-material/Delete";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useDeletePatient, useGetPatients } from "../../api/ekoApi";
import { useTranslation } from "react-i18next";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { useNavigate } from "react-router-dom";
import { StepperHorizontal } from "../../components/forms/Stepper";
import { SearchInput } from "../../components/SearchInput";

const TabAndSearchWrapper = styled(Box)`
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  padding-right: 10px;
  padding-bottom: 12px;
  align-items: center;
`;


const Patients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { data, isLoading, isError, refetch } = useGetPatients(searchTerm);
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [stepperModalOpen, setStepperModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const deletePatient = useDeletePatient();


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value, refetch) => {
      refetch({ searchTerm: value });
    }, 400),
    []
  );

  const handleDelete = (rowData) => {
    //console.log(rowData, 'row data from delete')
    setDeleteModalOpen(true);
    setSelectedRowData(rowData);
  };

  const handleStepperModalOpen = (data) => {
    setPatientData(data);
    setStepperModalOpen(true);
  };
  const handleStepperModalClose = () => {
    setStepperModalOpen(false);
  };
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRowData(null);
  };

  const handleConfirmDelete = () => {
    deletePatient.mutate({ id: selectedRowData._id });
    handleCloseDeleteModal();
  };

  const handleNavigate = (data) => {
    navigate("/report", { state: { personalData: data } });
  };

  const columnDefsAdmins = [
    {
      field: "Name",
      headerName: t("name"),
      valueFormatter: ({ data }) => {
        const firstAndLastNAme = `${data.firstName} ${data.lastName}`;
        return firstAndLastNAme;
      },
      sortable: true 
    },

    { field: "phoneNumber1", headerName: t("phoneNumber1"), flex: 1 },
    { field: "email", headerName: t("emailLabel"), flex: 1, sortable: true  },
    {
      field: "examinationdate",
      headerName: t("examinationDate"),
      valueFormatter: ({ value }) => {
        const date = new Date(value);
        const formattedDate = `${date.getDate()}/${
          date.getMonth() + 1
        }/${date.getFullYear()}`;
        return formattedDate;
      },
      sortable: true 
    },
    { field: "_id", hide: true },
    { field: "serialNumber", headerName: t("serialNumber") },
    {
      headerName: t("report"),
      cellRenderer: (params) => {
        return (
          <>
            <SummarizeOutlinedIcon
              onClick={() => handleNavigate(params.data)}
              style={{ cursor: "pointer" }}
            />
          </>
        );
      },

      width: 100,
    },
    {
      headerName: t("actions"),
      cellRenderer: (params) => (
        <>
          <EditNoteIcon
            onClick={() => handleStepperModalOpen(params)}
            style={{ cursor: "pointer", color: "blue", marginRight: "10px" }}
          />
          <DeleteIcon
            onClick={() => handleDelete(params.data)}
            style={{ cursor: "pointer" }}
          />
        </>
      ),
      width: 150,
    },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading patient data: {isError.message}</div>;
  }

  const rowData = data?.patients || [];
  const localeText = {
    noRowsToShow: t("noRowsToShowPatients"),
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    handleSearch(value, refetch);
  };

  return (
    <div>
      <TabAndSearchWrapper>
      <SearchInput 
      searchText={searchTerm}
      handleSearchInputChange={handleSearchInput}
      />
      </TabAndSearchWrapper>
      <div
        className="ag-theme-alpine"
        style={{ height: "80vh", width: "100%" }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefsAdmins}
          localeText={localeText}
        ></AgGridReact>
      </div>
      {/* Stepper Modal */}
      <Dialog
        open={stepperModalOpen}
        onClose={handleStepperModalClose}
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            backgroundColor: "black",
            color: "white",
            paddingTop: 1,
            paddingBottom: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {t("editPatient")}
          <IconButton onClick={handleStepperModalClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <StepperHorizontal patientData={patientData} edit />
        </DialogContent>
      </Dialog>
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>{t("deleteConfirmation")}</DialogTitle>
        <DialogContent>
          {t("areYouSureToDelete")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmDelete} color="error">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Patients;
